import {useCallback, useEffect, useRef, useState} from 'react';
import {request} from './api';
import {ACCESS_UPDATE_KEY} from '../utils/accessUpdates';

export function mergeItems(first, next) {
  const items = new Map(first.map(item => [item.id, item]));
  next.forEach(item => items.set(item.id, item));
  return Array.from(items.values());
}
export function scrollRoot(element) {
  for (let parent = element?.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    if (/(auto|scroll|overlay)/.test(window.getComputedStyle(parent).overflowY)) return parent;
  }
  return null;
}
function validatePage(page, offset) {
  if (!page || !Array.isArray(page.items) || !Array.isArray(page.breadcrumbs)
      || (page.next_offset != null && (!Number.isSafeInteger(page.next_offset) || page.next_offset <= offset))) {
    throw new Error('Сервер вернул некорректную страницу файлов. Обновите папку.');
  }
  return page;
}
const denied = error => [401, 403, 404].includes(error.status);

export default function useListing({folderId, ctx, search, sort, rootId}) {
  const key = JSON.stringify([folderId, ctx.auth, ctx.share, search, sort, rootId]);
  const [snapshot, setSnapshot] = useState(null), [loading, setLoading] = useState(true);
  const [error, setError] = useState(''), [pageError, setPageError] = useState('');
  const [busy, setBusy] = useState(false), [refresh, setRefresh] = useState(0);
  const [accessEpoch, setAccessEpoch] = useState(0);
  const generation = useRef(0), current = useRef(null), pageFlight = useRef(false);
  const mainController = useRef(null), pageController = useRef(null), fetching = useRef(false);
  const lastRefresh = useRef(0);
  current.current = snapshot;
  const data = snapshot?.key === key ? snapshot.data : null;
  const reload = useCallback(() => setRefresh(value => value + 1), []);
  const invalidate = useCallback(() => {
    ++generation.current;
    mainController.current?.abort(); pageController.current?.abort();
    current.current = null; setSnapshot(null); setAccessEpoch(value => value + 1); reload();
  }, [reload]);

  useEffect(() => {
    const controller = new AbortController(), version = ++generation.current;
    mainController.current = controller; pageController.current?.abort();
    pageFlight.current = false; fetching.current = true; lastRefresh.current = Date.now();
    setBusy(false); setLoading(true); setError(''); setPageError('');
    const prior = current.current?.key === key ? current.current : null;
    const lastOffset = prior?.lastOffset || 0;
    const page = async offset => {
      const params = new URLSearchParams({q: search, sort, offset});
      if (folderId) params.set('parent', folderId);
      return validatePage(await request(`nodes/?${params}`, {ctx, signal: controller.signal}), offset);
    };
    (async () => {
      let offset = 0, result = await page(0), items = result.items;
      // Refresh every loaded page atomically, retaining visible rows until success.
      while (result.next_offset != null && result.next_offset <= lastOffset) {
        offset = result.next_offset; result = await page(offset);
        items = mergeItems(items, result.items);
      }
      if (!controller.signal.aborted && version === generation.current) {
        const next = {key, lastOffset: offset, data: {...result, items}};
        current.current = next; setSnapshot(next);
      }
    })().catch(failure => {
      if (controller.signal.aborted || version !== generation.current) return;
      setError(failure.message);
      if (denied(failure)) {current.current = null; setSnapshot(null);}
    }).finally(() => {
      if (!controller.signal.aborted && version === generation.current) {
        fetching.current = false; setLoading(false);
      }
    });
    return () => {controller.abort(); pageController.current?.abort(); ++generation.current;};
  }, [key, folderId, ctx, search, sort, refresh]);

  const more = useCallback(async () => {
    const before = current.current;
    if (pageFlight.current || fetching.current || before?.key !== key || before.data.next_offset == null) return;
    const version = generation.current, offset = before.data.next_offset, controller = new AbortController();
    pageController.current = controller; pageFlight.current = true; setBusy(true); setPageError('');
    try {
      const params = new URLSearchParams({q: search, sort, offset});
      if (folderId) params.set('parent', folderId);
      const next = validatePage(await request(`nodes/?${params}`, {ctx, signal: controller.signal}), offset);
      if (!controller.signal.aborted && version === generation.current && current.current?.key === key) {
        const updated = {key, lastOffset: offset, data: {...next, items: mergeItems(current.current.data.items, next.items)}};
        current.current = updated; setSnapshot(updated);
      }
    } catch (failure) {
      if (!controller.signal.aborted && version === generation.current) {
        setPageError(failure.message);
        if (denied(failure)) {current.current = null; setSnapshot(null); setPageError(''); setError(failure.message);}
      }
    } finally {
      if (version === generation.current) {pageFlight.current = false; setBusy(false);}
    }
  }, [key, folderId, ctx, search, sort]);

  useEffect(() => {
    let timer;
    const soft = () => {
      if (document.visibilityState === 'hidden') return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (Date.now() - lastRefresh.current >= 5000 && !fetching.current && !pageFlight.current) reload();
      }, 200);
    };
    const onStorage = event => {if (event.key === ACCESS_UPDATE_KEY) invalidate();};
    window.addEventListener('focus', soft); document.addEventListener('visibilitychange', soft);
    window.addEventListener('access:changed', invalidate); window.addEventListener('access:refreshed', invalidate);
    window.addEventListener('storage', onStorage);
    const interval = setInterval(soft, 60000);
    return () => {
      clearTimeout(timer); clearInterval(interval);
      window.removeEventListener('focus', soft); document.removeEventListener('visibilitychange', soft);
      window.removeEventListener('access:changed', invalidate); window.removeEventListener('access:refreshed', invalidate);
      window.removeEventListener('storage', onStorage);
    };
  }, [reload, invalidate]);

  return {data, loading: !data && (loading || (!error && snapshot?.key !== key)), refreshing: loading && Boolean(data), busy,
    error, pageError, more, reload, invalidate, accessEpoch};
}
